/**
 * Theme preference: `system` (default), `light` or `dark`.
 *
 * The preference is applied as `data-theme` on <html>. `system` means NO stamp, which
 * is the fragile CSS path (bare :root + guarded media query) — it is the default on
 * purpose so that path is the one exercised every day (see index.css).
 *
 * Applied from main.tsx before the first render. There is no inline <script> in
 * index.html because the production CSP is script-src 'self'; the un-stamped state
 * already paints the right system theme without JavaScript, so only an explicit
 * preference could flash, and it is applied before React paints anything.
 */
import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'gitrader.theme';
const PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (PREFERENCES as readonly string[]).includes(value);
}

export function readThemePreference(storage: Pick<Storage, 'getItem'> | null): ThemePreference {
  try {
    const raw = storage?.getItem(THEME_STORAGE_KEY);
    return isThemePreference(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

export function applyThemePreference(pref: ThemePreference, root: HTMLElement): void {
  if (pref === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', pref);
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

export function resolveTheme(pref: ThemePreference, prefersDark: boolean): ResolvedTheme {
  if (pref === 'system') return prefersDark ? 'dark' : 'light';
  return pref;
}

/** The order the toggle cycles through: what the OS says, then the two explicit choices. */
export function nextThemePreference(pref: ThemePreference): ThemePreference {
  const i = PREFERENCES.indexOf(pref);
  return PREFERENCES[(i + 1) % PREFERENCES.length] ?? 'system';
}

/** Call once at startup, before the first render. */
export function initTheme(): ThemePreference {
  const pref = readThemePreference(safeStorage());
  applyThemePreference(pref, document.documentElement);
  return pref;
}

function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function useTheme(): {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (pref: ThemePreference) => void;
  cycle: () => void;
} {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    readThemePreference(safeStorage()),
  );
  const [prefersDark, setPrefersDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setPrefersDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const setPreference = useCallback((pref: ThemePreference) => {
    setPreferenceState(pref);
    applyThemePreference(pref, document.documentElement);
    try {
      if (pref === 'system') window.localStorage.removeItem(THEME_STORAGE_KEY);
      else window.localStorage.setItem(THEME_STORAGE_KEY, pref);
    } catch {
      // Storage unavailable (private mode, blocked) — the preference lives for this page only.
    }
  }, []);

  const cycle = useCallback(
    () => setPreference(nextThemePreference(preference)),
    [preference, setPreference],
  );

  return { preference, resolved: resolveTheme(preference, prefersDark), setPreference, cycle };
}
